import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Sparkles, Send, Archive, RefreshCw, Mail, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = {
  talent: { label: "Music / Fashion / Style", color: "text-[#E30613] border-[#E30613]" },
  advertising: { label: "Advertising / Collab", color: "text-amber-400 border-amber-400/60" },
  join: { label: "Join the Team", color: "text-emerald-400 border-emerald-400/60" },
};
const FILTERS = [["all", "All"], ["talent", "Talent"], ["advertising", "Ads/Collab"], ["join", "Join Team"]];

export const Inbox = () => {
  const [subs, setSubs] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/submissions`);
      setSubs(data);
      if (sel) {
        const fresh = data.find((s) => s.id === sel.id);
        if (fresh) { setSel(fresh); }
      }
    } catch (e) { /* handled by shell */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (s) => { setSel(s); setDraft(s.ai_reply || ""); setNote(""); };

  const regenerate = async () => {
    if (!sel) return;
    setBusy("gen"); setNote("");
    try {
      const { data } = await axios.post(`${API}/submissions/${sel.id}/generate`);
      setDraft(data.ai_reply);
    } catch (e) { setNote("Couldn't regenerate. Try again."); } finally { setBusy(""); }
  };

  const send = async () => {
    if (!sel || !draft.trim()) return;
    setBusy("send"); setNote("");
    try {
      await axios.post(`${API}/submissions/${sel.id}/send`, { text: draft });
      setNote("Reply sent.");
      await load();
    } catch (e) { setNote("Send failed. Check the address and try again."); } finally { setBusy(""); }
  };

  const archive = async () => {
    if (!sel) return;
    setBusy("arch");
    try { await axios.patch(`${API}/submissions/${sel.id}`, { status: "archived" }); await load(); }
    catch (e) { /* noop */ } finally { setBusy(""); }
  };

  const list = subs.filter((s) => filter === "all" || (s.category || "talent") === filter);

  return (
    <div data-testid="inbox-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`inbox-filter-${k}`} onClick={() => setFilter(k)}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${filter === k ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/20 text-neutral-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="inbox-refresh" className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/20 text-neutral-400 hover:text-white transition-colors">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-2 border border-white/10 divide-y divide-white/5 max-h-[70vh] overflow-y-auto" data-testid="inbox-list">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E30613]" size={24} /></div>
          ) : list.length === 0 ? (
            <p className="p-6 text-neutral-600 text-sm">No submissions in this view.</p>
          ) : list.map((s) => {
            const cat = CATS[s.category || "talent"] || CATS.talent;
            const isSel = sel?.id === s.id;
            return (
              <button key={s.id} data-testid={`inbox-item-${s.id}`} onClick={() => open(s)}
                className={`w-full text-left p-4 transition-colors ${isSel ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white text-sm font-bold truncate">{s.name}</span>
                  {s.status === "replied" ? (
                    <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-400 shrink-0">Replied</span>
                  ) : s.status === "archived" ? (
                    <span className="text-[9px] uppercase tracking-wider font-bold text-neutral-600 shrink-0">Archived</span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-wider font-bold text-[#E30613] shrink-0">New</span>
                  )}
                </div>
                <span className={`inline-block mt-1.5 text-[9px] uppercase tracking-[0.15em] font-bold border px-2 py-0.5 ${cat.color}`}>{cat.label}</span>
                <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{s.message}</p>
              </button>
            );
          })}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 border border-white/10 p-5 md:p-6" data-testid="inbox-detail">
          {!sel ? (
            <div className="flex flex-col items-center justify-center text-center py-20 text-neutral-600">
              <Mail size={28} className="mb-3" /> Select a submission to review and reply.
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-3 border-b border-white/10 pb-4 mb-4">
                <div>
                  <p className="text-white text-lg font-bold" data-testid="inbox-detail-name">{sel.name}</p>
                  <a href={`mailto:${sel.email}`} className="text-sm text-[#E30613] hover:underline">{sel.email}</a>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mt-2">
                    {(CATS[sel.category || "talent"] || CATS.talent).label} · {sel.network}{sel.discipline ? ` · ${sel.discipline}` : ""}
                  </p>
                </div>
                <button onClick={archive} data-testid="inbox-archive" disabled={!!busy} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-500 hover:text-white transition-colors disabled:opacity-50">
                  <Archive size={13} /> Archive
                </button>
              </div>

              {sel.links && (
                <a href={sel.links.startsWith("http") ? sel.links : `https://${sel.links}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-300 hover:text-[#E30613] transition-colors mb-4" data-testid="inbox-portfolio-link">
                  <ExternalLink size={13} /> {sel.links}
                </a>
              )}

              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Their message</p>
              <p className="text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap border-l-2 border-white/10 pl-4 mb-6">{sel.message}</p>

              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613] inline-flex items-center gap-1.5"><Sparkles size={13} /> AI Reply Draft</p>
                <button onClick={regenerate} data-testid="inbox-regenerate" disabled={!!busy} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-400 hover:text-white transition-colors disabled:opacity-50">
                  {busy === "gen" ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Regenerate
                </button>
              </div>
              <textarea
                data-testid="inbox-reply-text" value={draft} onChange={(e) => setDraft(e.target.value)} rows={9}
                placeholder={busy === "gen" ? "Writing…" : "AI reply will appear here."}
                className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-4 text-sm leading-relaxed"
              />

              {note && <p data-testid="inbox-note" className="text-sm text-neutral-300 mt-3">{note}</p>}

              <div className="flex items-center gap-3 mt-4">
                <button onClick={send} data-testid="inbox-send" disabled={!!busy || !draft.trim()} className="inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
                  {busy === "send" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                  {sel.ai_reply_sent ? "Resend Reply" : "Send Reply"}
                </button>
                {sel.ai_reply_sent && <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-400">Already replied</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
